import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Rectangle } from '@react-google-maps/api';
import { Box, Button, Stack, Typography } from '../../../ui/core';
// Imported from its module, not the `hooks` barrel: that barrel pulls in the
// API-backed hooks, and this component's suite has no business mocking them to
// test a rectangle.
import useDebouncedValue from '../../../hooks/useDebouncedValue';

/**
 * Rectangle area select for the candidate sweep. Renders as a CHILD OF `<Map>`:
 * `<Rectangle>` takes the map instance from `@react-google-maps/api`'s context,
 * and the control strip is absolutely positioned inside the map container.
 *
 * WHY A CORE `Rectangle` AND NOT THE `drawing` LIBRARY — AND THE LESSON THAT COST
 * US A BLOCKING DEFECT.
 *
 * This component was first written with `DrawingManager` (drag-to-draw). Google
 * REMOVED `DrawingManager` in Maps JavaScript API 3.65: constructing one now
 * throws `The DrawingManager functionality in the Maps JavaScript API is no
 * longer available ... as of version 3.65`. With no error boundary above the map,
 * that took the whole admin page blank.
 *
 * The guard that was supposed to catch it did not, and this is the part worth
 * remembering: it probed `window.google.maps.drawing.OverlayType.RECTANGLE` and
 * THAT PROBE PASSES on 3.65. Google left a throwing stub behind — the `drawing`
 * namespace exists, `OverlayType.RECTANGLE` is truthy, and
 * `typeof drawing.DrawingManager === 'function'`. Only construction reveals the
 * removal. So: FEATURE-DETECTING A MAPS CAPABILITY BY PROPERTY PRESENCE IS
 * UNSOUND. Construction, or an explicit version check, is the only honest test —
 * and a namespace whose members throw is not something to degrade from, it is
 * something to stop depending on. Hence no `DrawingManager` import, no fallback
 * path, and `drawing` is no longer in `ui/core/Map.js`'s `LIBRARIES`.
 *
 * `Rectangle`, `Polygon`, `Polyline`, `LatLngBounds` and `google.maps.event` are
 * all core and unaffected; an `editable`/`draggable` `Rectangle` gives the
 * operator corner handles and a drag target, which is the same interaction
 * drag-to-draw was for, minus the dead library.
 *
 * SEEDED FROM THE VIEWPORT, NOT FROM A DRAG. With no drag-to-draw gesture, the
 * rectangle has to come from somewhere: the current map view, inset to the middle
 * `100 - 2 * VIEWPORT_INSET_FRACTION * 100`% so it reads as a selection instead
 * of hugging the map edges where its handles would be unreachable. "Sweep what I
 * am looking at" is also just a natural operator intent, so the same affordance
 * stays available once a box exists ("Use current view").
 *
 * BOUNDS CHANGES ARE DEBOUNCED, AND THE PENDING VALUE IS A STRING. Dragging fires
 * `bounds_changed` continuously and every emitted bbox re-keys the estimate
 * query. `useDebouncedValue` compares by REFERENCE, so debouncing a `{ swLng...
 * }` object would re-fire on every render; the pending bbox is therefore held as
 * its serialised key, which gives value equality for free and makes a settled
 * drag exactly one emit. That pending key is also RESYNCED whenever the bbox
 * prop moves from outside this component, so a drag that was in flight when the
 * selection changed underneath it can never be emitted against the new one —
 * see the resync note in the component body.
 *
 * THE `bounds` PROP IS MEMOISED, WHICH IS LOAD-BEARING. `<Rectangle>` calls
 * `setBounds` from an effect keyed on the prop, so a fresh object literal each
 * render would re-apply bounds on every render and yank the rectangle out from
 * under the operator mid-resize. Memoised on the four scalars, it re-applies only
 * when the numbers actually change — and when they change because WE emitted
 * them, `setBounds` is a no-op against what the overlay already shows.
 *
 * ARMED-FOR-SELECTION IS DERIVED FROM THE BBOX, NEVER STORED SEPARATELY.
 * "There is no area" and "the operator may seed one" are the same state, so
 * `!bbox` IS the arming condition. An earlier version kept an `isDrawing` flag
 * seeded from `useState(!bbox)`: because that seed is evaluated once and this
 * component never unmounts, any path that cleared the bbox without also setting
 * the flag (the Clear button, or Back to a URL with no bbox) left the strip
 * inviting a selection that could not happen — area select permanently dead until
 * a page reload. Deriving it makes that state unreachable by construction rather
 * than by remembering to keep two values in step.
 *
 * THE BBOX DERIVATION MATCHES `LaneMap.js`'s `getBoundsFromMap` EXACTLY (same
 * field names, same `getSouthWest()`/`getNorthEast()` + `.lng()`/`.lat()` reads).
 * Both feed backend endpoints that take the same four scalars; a second
 * convention for the same concept is how a lat/lng swap gets shipped.
 *
 * NO CELL-GRID PREVIEW — see `AREA_SELECT_NO_GRID_PREVIEW` below.
 */

/**
 * Why there is no grid preview.
 *
 * The estimate's `cells` is a COUNT, not geometry — `buildSearchGrid` returns
 * `{ cells, cellCount }` and deliberately leaves `cells` EMPTY over the cap, so
 * there is nothing on the wire to draw. Drawing the grid would mean
 * reimplementing the tiler client-side: spacing of `radius * sqrt(2)`,
 * longitude steps scaled by `cos(midLat)`, `ceil` on each span, and a floor of
 * one row/column for a zero-area box.
 *
 * That is only ~20 lines, and it is exactly why it is not here. That formula IS
 * the bill — `estimate` and `generate` share it so the quote and the charge
 * cannot disagree — and a client copy that drifted by one row would draw a grid
 * the backend will not use. The operator would then tune the box against a
 * fiction, which is strictly worse than no preview: a missing preview prompts
 * them to read the authoritative cell count in the estimate readout, while a
 * wrong one invites them to trust the picture. If a preview is wanted later, the
 * honest way to get it is a backend endpoint that returns the cells it tiled.
 */
export const AREA_SELECT_NO_GRID_PREVIEW = true;

/** Trimmed off EACH edge of the viewport when seeding — the middle 60% remains. */
export const VIEWPORT_INSET_FRACTION = 0.2;

/** One emit per settled drag, not one per `bounds_changed`. */
export const BOUNDS_DEBOUNCE_MS = 300;

/**
 * `google.maps.LatLngBounds` → the four wire scalars.
 *
 * Character-for-character the same derivation as `LaneMap.js`'s
 * `getBoundsFromMap` (~:301). Deliberately duplicated rather than imported:
 * that one closes over a `useCallback` inside a 1,000-line page component, and
 * this module has no business importing from `pages/Location`.
 */
export const boundsToBbox = (bounds) => {
  if (!bounds) return null;
  const sw = bounds.getSouthWest?.();
  const ne = bounds.getNorthEast?.();
  if (!sw || !ne) return null;
  const bbox = { swLng: sw.lng(), swLat: sw.lat(), neLng: ne.lng(), neLat: ne.lat() };
  // A rectangle whose corners did not read back as numbers is not a smaller
  // rectangle, it is a guaranteed 400 — `StationCandidatesApi` throws on it.
  // Report nothing rather than a broken bbox.
  return Object.values(bbox).every((value) => Number.isFinite(value)) ? bbox : null;
};

/**
 * The four scalars → a `LatLngBoundsLiteral`, which is a plain object and needs
 * no Maps API present. `min`/`max` rather than a direct mapping so a rectangle
 * dragged bottom-right-to-top-left still renders.
 */
export const bboxToBoundsLiteral = (bbox) => {
  if (!bbox) return null;
  const { swLng, swLat, neLng, neLat } = bbox;
  if (![swLng, swLat, neLng, neLat].every((value) => Number.isFinite(value))) return null;
  return {
    south: Math.min(swLat, neLat),
    north: Math.max(swLat, neLat),
    west: Math.min(swLng, neLng),
    east: Math.max(swLng, neLng),
  };
};

/**
 * The stable string identity of a bbox. Exists so the debounced pending value
 * compares by VALUE — `useDebouncedValue` keys its effect on reference equality,
 * and `bounds_changed` hands us a fresh object every time it fires.
 */
export const bboxKey = (bbox) => {
  if (!bbox) return '';
  const { swLng, swLat, neLng, neLat } = bbox;
  return [swLng, swLat, neLng, neLat].every((value) => Number.isFinite(value))
    ? `${swLng},${swLat},${neLng},${neLat}`
    : '';
};

/** Inverse of `bboxKey`, for reading a settled debounced value back out. */
export const bboxFromKey = (key) => {
  if (!key) return null;
  const [swLng, swLat, neLng, neLat] = String(key).split(',').map(Number);
  const bbox = { swLng, swLat, neLng, neLat };
  return Object.values(bbox).every((value) => Number.isFinite(value)) ? bbox : null;
};

/**
 * The current map view, inset, as a bbox — the seed for a new selection.
 *
 * Inset rather than the raw viewport: a rectangle flush with the map edges has
 * its corner handles off-screen, so the operator could neither see the selection
 * as a selection nor resize it.
 */
export const seedBboxFromMap = (map, inset = VIEWPORT_INSET_FRACTION) => {
  const literal = bboxToBoundsLiteral(boundsToBbox(map?.getBounds?.()));
  if (!literal) return null;
  const latInset = (literal.north - literal.south) * inset;
  const lngInset = (literal.east - literal.west) * inset;
  return {
    swLng: literal.west + lngInset,
    swLat: literal.south + latInset,
    neLng: literal.east - lngInset,
    neLat: literal.north - latInset,
  };
};

const RECTANGLE_STYLE = {
  fillColor: '#1976d2',
  fillOpacity: 0.12,
  strokeColor: '#1976d2',
  strokeWeight: 2,
  // Editable and draggable IS the interaction now that `DrawingManager` is gone:
  // corner handles resize, the body drags. Safe because the emitted bbox is
  // debounced and the `bounds` prop is memoised, so re-applying bounds after our
  // own emit cannot fight the operator or loop.
  editable: true,
  draggable: true,
  clickable: true,
  zIndex: 2,
};

const DISABLED_RECTANGLE_STYLE = {
  ...RECTANGLE_STYLE,
  editable: false,
  draggable: false,
  clickable: false,
};

const CONTROL_BOX_SX = {
  position: 'absolute',
  top: 8,
  left: 8,
  zIndex: 3,
  p: 1,
  borderRadius: 1,
  bgcolor: 'background.paper',
  boxShadow: 1,
  maxWidth: 320,
};

const coord = (value) => (Number.isFinite(value) ? value.toFixed(4) : '—');

/**
 * @param bbox      the current `{ swLng, swLat, neLng, neLat }`, or null.
 * @param onChange  called with a new bbox when a seed is placed and when a
 *                  resize/drag settles, and with null on Clear. The bbox is the
 *                  ONLY thing that decides whether selection is armed, so a
 *                  caller that ignores the null leaves the last rectangle
 *                  standing rather than stranding the control.
 * @param map       the `google.maps.Map` instance, needed to read the viewport a
 *                  seed is cut from. Passed down rather than pulled from context
 *                  so the page's existing `onLoad` state is the single source of
 *                  it. Without it, seeding is unavailable but an existing bbox
 *                  still renders.
 * @param cells     the estimate's authoritative cell count, shown in place of a
 *                  grid preview. Optional.
 */
const AreaSelect = ({ bbox = null, onChange, map = null, cells = null, disabled = false }) => {
  // Derived, not stored: no area means selection is armed, which is why the first
  // thing an operator does on an empty builder is the thing the page is for —
  // and why no code path can leave the strip inviting a selection that cannot
  // happen.
  const isSelecting = !bbox;

  const rectangleRef = useRef(null);
  const [pendingKey, setPendingKey] = useState('');
  const debouncedKey = useDebouncedValue(pendingKey, BOUNDS_DEBOUNCE_MS);

  // See the header note: a fresh literal here would re-apply bounds on every
  // render and yank the overlay out from under a resize in progress.
  const boundsLiteral = useMemo(() => bboxToBoundsLiteral(bbox), [bbox?.swLng, bbox?.swLat, bbox?.neLng, bbox?.neLat]); // eslint-disable-line react-hooks/exhaustive-deps

  const currentKey = bboxKey(bbox);

  /**
   * NOTHING PENDING SURVIVES AN EXTERNAL CHANGE TO THE SELECTION.
   *
   * The page owns the bbox (it lives in the URL), so it can move underneath us
   * with no emit from here: changing city pushes `bbox: null`, and so do Clear,
   * a deep link, and Back/Forward. Whatever half-finished drag we were holding
   * belongs to the area that just went away — re-emitting it writes the previous
   * city's off-screen box back under the new one, restoring its estimate and
   * re-arming Generate for something the operator cannot see.
   *
   * So on any incoming key we have not already taken, the pending value is
   * resynced to it. This is the documented render-phase adjustment rather than
   * an effect ON PURPOSE: an effect's `pendingKey` closure would still hold the
   * stale key when the emit effect below ran in the same commit, and it would
   * emit before the resync ever landed. Adjusting during render means the emit
   * effect never sees the stale pairing at all.
   *
   * It cannot loop: it fires only while `syncedKey !== currentKey`, and it sets
   * `syncedKey` to `currentKey` in the same pass, so each distinct incoming key
   * is consumed exactly once. `pendingKey` is a STRING, so the common case —
   * the page echoing our own emit straight back — assigns an equal value and
   * React bails out with no extra render and no restarted debounce. And the
   * resync never calls `onChange`, so it cannot feed the parent either.
   */
  const [syncedKey, setSyncedKey] = useState(currentKey);
  if (syncedKey !== currentKey) {
    setSyncedKey(currentKey);
    setPendingKey(currentKey);
  }

  /**
   * The debounce has caught up with what we hold — or empty while it trails.
   *
   * `debouncedKey` alone is not enough to emit on: right after a resync it is
   * still the key of the box that was just replaced, which is exactly the value
   * that must never reach `onChange`. Requiring it to EQUAL `pendingKey` makes
   * "the operator's drag settled" the only condition that emits, and a resync
   * (which moves `pendingKey` and restarts the timer) reads as "not settled"
   * until the debounce agrees.
   */
  const settledKey = debouncedKey === pendingKey ? debouncedKey : '';

  useEffect(() => {
    if (!settledKey || settledKey === currentKey) return;
    const next = bboxFromKey(settledKey);
    if (next) onChange?.(next);
    // `onChange` is intentionally in the deps: the page rebuilds it whenever the
    // bbox changes, and the `settledKey === currentKey` guard is what stops the
    // re-run from emitting the same value a second time.
  }, [settledKey, currentKey, onChange]);

  const handleBoundsChanged = useCallback(() => {
    const next = boundsToBbox(rectangleRef.current?.getBounds?.());
    // Same string for an unchanged rectangle, so React bails and the debounce
    // timer is not restarted — this fires again when we re-apply our own bounds.
    if (next) setPendingKey(bboxKey(next));
  }, []);

  const seedFromViewport = useCallback(() => {
    const seed = seedBboxFromMap(map);
    if (!seed) return;
    // Straight through, not debounced: a click is already one discrete intent,
    // and the pending key is synced so the settle effect has nothing to re-emit.
    setPendingKey(bboxKey(seed));
    onChange?.(seed);
  }, [map, onChange]);

  const clear = useCallback(() => {
    // Belt and braces: the resync above would drop the pending key anyway once
    // the page echoes the null back, but Clear knows the box is gone right now
    // and a caller that ignores the null should not leave a drag pending.
    setPendingKey('');
    onChange?.(null);
  }, [onChange]);

  const canSeed = !!map && !disabled;

  return (
    <>
      {boundsLiteral ? (
        <Rectangle
          bounds={boundsLiteral}
          options={disabled ? DISABLED_RECTANGLE_STYLE : RECTANGLE_STYLE}
          onLoad={(rectangle) => {
            rectangleRef.current = rectangle;
          }}
          onUnmount={() => {
            rectangleRef.current = null;
          }}
          onBoundsChanged={handleBoundsChanged}
        />
      ) : null}

      <Box sx={CONTROL_BOX_SX}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button size="small" variant={isSelecting ? 'contained' : 'text'} onClick={seedFromViewport} disabled={!canSeed}>
            Use current view
          </Button>
          {bbox ? (
            <Button size="small" onClick={clear} disabled={disabled}>
              Clear
            </Button>
          ) : null}
        </Stack>

        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          {bbox
            ? 'Drag the rectangle to move it, or its corner handles to resize.'
            : 'Pan and zoom to the area you want, then place a selection over it.'}
        </Typography>

        {bbox ? (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            SW {coord(bbox.swLat)}, {coord(bbox.swLng)} · NE {coord(bbox.neLat)},{' '}
            {coord(bbox.neLng)}
          </Typography>
        ) : null}

        {bbox && Number.isFinite(cells) ? (
          // Stands in for a grid preview: the count the SERVER tiled, not a
          // client re-derivation of it. See AREA_SELECT_NO_GRID_PREVIEW.
          <Typography variant="caption" color="text.secondary" display="block">
            {cells.toLocaleString()} search cells
          </Typography>
        ) : null}
      </Box>
    </>
  );
};

export default AreaSelect;
